import React, { PureComponent } from 'react';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { round } from '../../../utils';
import { LocalAtRecord } from '../../../db';
import { Chip } from '@mui/material';
import { Tooltip } from '@mui/material';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { SettingsAccessLevel } from '../../../types/types';
import ListItemButton from '@mui/material/ListItemButton';

interface MapsJobsProps {
  id: string;
  job: LocalAtRecord<JobsFieldSet>;
  loadMissionMaps: (id: string) => Promise<void>;
  name: string;
  accessLevel: SettingsAccessLevel;
}

interface MapsJobState {
  ready: boolean;
  numSamples?: number;
  boundAcres: number;
  done: string;
}

class MapsJob extends PureComponent<MapsJobsProps, MapsJobState> {
  constructor(props: MapsJobsProps) {
    super(props);
    this.state = {
      ready: false,
      numSamples: 0,
      boundAcres: 0,
      done: '',
    };
  }

  async componentDidMount() {
    await this.initJobInfo();
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.id !== this.props.id) {
      await this.initJobInfo();
    }
  }

  async initJobInfo() {
    if (!this.state.ready) {
      await this.checkAttachmentsReady();
    }

    this.setState({
      numSamples: this.props.job.get('# Samples'),
      boundAcres: round(this.props.job.get('Boundary Acres') || 0, 1) || 0,
      done: this.props.job.get('Sample Date') ?? '',
    });
  }

  async checkAttachmentsReady() {
    if (this.props.job.attachments_up_to_date()) {
      this.setState({ ready: true });
    } else {
      this.setState({ ready: false });
      await this.props.job.refresh_attachments({ map_making: true });
      this.setState({ ready: true });
      // this.props.job.refresh_attachments().then(() => {
      //   this.setState({ ready: true });
      // });
    }
  }

  getSitesTypeColor = (sitesType: string) => {
    switch (sitesType) {
      case 'Grid':
        return 'green';
      case 'Zone':
        return 'blue';
      case 'ModGrid':
        return 'purple';
      default:
        return 'red';
    }
  };

  render() {
    const jobHasMapIssue = this.props.job.get('Job Flags')?.includes('Map Issue');
    const mapIssueDescription = this.props.job.get('Job Flag Notes');
    const sitesType = this.props.job.get('Deal Sites Type')?.[0] ?? '?';
    const points = this.props.job.get('Pts Shp');
    const hasPoints = points && points.length > 0;
    const missionStatus = this.props.job.get('Mission Status');

    const zones = this.props.job.get('Sample Zones Shp');
    const hasZones = zones && zones.length > 0;

    return (
      <React.Fragment>
        <Tooltip
          title={
            <Typography style={{ whiteSpace: 'pre-line' }}>
              {[
                `Operator: ${(this.props.job.get('Operator Name')?.length ?? 0) > 0 ? this.props.job.get('Operator Name')![0] : 'Unassigned'}`,
                `Robot: ${this.props.job.get('Robot') ?? 'None'}`,
                `Sites Type: ${sitesType}`,
                hasPoints ? 'Includes Points' : '',
                hasZones ? 'Includes Zones' : '',
              ]
                .filter((s) => !!s)
                .join('\r\n')}
            </Typography>
          }
        >
          <ListItemButton
            disabled={Boolean(!this.state.ready || this.state.done) && this.props.accessLevel !== 'Technician'}
            key={this.props.id}
            divider
            onClick={async () => {
              await this.props.loadMissionMaps(this.props.id);
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
              {/* Left Side: Job name */}
              <Box flexGrow={1} minWidth={0}>
                <ListItemText primary={<Typography variant="body2">{this.props.name}</Typography>} />
              </Box>

              {/* Middle: Optional chip */}
              {jobHasMapIssue && (
                <Box pl={1}>
                  <Tooltip title={<Typography>{mapIssueDescription ?? ''}</Typography>}>
                    <Chip size="small" style={{ color: 'white', backgroundColor: 'red' }} label="Map Issue" />
                  </Tooltip>
                </Box>
              )}

              {/* Right Side: Sample info and mission status */}
              <Box pl={2} display="flex" flexDirection="column" alignItems="flex-end" minWidth="140px">
                <Typography noWrap variant="body2">
                  <b>{this.state.numSamples}</b> samples, <b>{this.state.boundAcres}</b> ac.
                </Typography>

                <Typography variant="caption" style={{ fontSize: '0.75rem', color: '#D2691E' }}>
                  {missionStatus}
                </Typography>
              </Box>
            </Box>
          </ListItemButton>
        </Tooltip>
      </React.Fragment>
    );
  }
}

export default MapsJob;
